/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react'
import useEmblaCarousel from 'embla-carousel-react'
import type { UseEmblaCarouselType } from 'embla-carousel-react'

function cn(
  ...parts: Array<string | undefined | false | null | Record<string, boolean>>
) {
  return parts
    .flatMap((p) => {
      if (!p) return []
      if (typeof p === 'string') return [p]
      return Object.entries(p)
        .filter(([, v]) => Boolean(v))
        .map(([k]) => k)
    })
    .join(' ')
}

type CarouselApi = UseEmblaCarouselType[1]
type UseCarouselParameters = Parameters<typeof useEmblaCarousel>
type CarouselOptions = UseCarouselParameters[0]
type CarouselPlugin = UseCarouselParameters[1]

type CarouselProps = {
  opts?: CarouselOptions
  plugins?: CarouselPlugin
  // eslint-disable-next-line no-unused-vars -- callback signature for parent
  setApi?: (api: CarouselApi) => void
}

type CarouselContextProps = {
  carouselRef: ReturnType<typeof useEmblaCarousel>[0]
  api: ReturnType<typeof useEmblaCarousel>[1]
  scrollPrev: () => void
  scrollNext: () => void
  canScrollPrev: boolean
  canScrollNext: boolean
} & CarouselProps

const CarouselContext = React.createContext<CarouselContextProps | null>(null)

function useCarousel() {
  const context = React.useContext(CarouselContext)

  if (!context) {
    throw new Error('useCarousel must be used within a <Carousel />')
  }

  return context
}

function Carousel({
  opts,
  setApi,
  plugins,
  className,
  children,
  ...props
}: React.ComponentProps<'div'> & CarouselProps) {
  const [carouselRef, emblaApi] = useEmblaCarousel(
    {
      ...opts,
      axis: 'x',
    },
    plugins
  )
  const [canScrollPrev, setCanScrollPrev] = React.useState(false)
  const [canScrollNext, setCanScrollNext] = React.useState(false)

  const onSelect = React.useCallback((instance: CarouselApi) => {
    if (!instance) return
    setCanScrollPrev(instance.canScrollPrev())
    setCanScrollNext(instance.canScrollNext())
  }, [])

  const scrollPrev = React.useCallback(() => {
    emblaApi?.scrollPrev()
  }, [emblaApi])

  const scrollNext = React.useCallback(() => {
    emblaApi?.scrollNext()
  }, [emblaApi])

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault()
        scrollPrev()
      } else if (event.key === 'ArrowRight') {
        event.preventDefault()
        scrollNext()
      }
    },
    [scrollPrev, scrollNext]
  )

  React.useEffect(() => {
    if (!emblaApi || !setApi) return
    setApi(emblaApi)
  }, [emblaApi, setApi])

  React.useEffect(() => {
    if (!emblaApi) return undefined
    onSelect(emblaApi)
    emblaApi.on('reInit', onSelect)
    emblaApi.on('select', onSelect)

    function cleanup() {
      emblaApi?.off('reInit', onSelect)
      emblaApi?.off('select', onSelect)
    }
    return cleanup
  }, [emblaApi, onSelect])

  const contextValue = React.useMemo(
    () => ({
      carouselRef,
      api: emblaApi,
      opts,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
    }),
    [
      carouselRef,
      emblaApi,
      opts,
      scrollPrev,
      scrollNext,
      canScrollPrev,
      canScrollNext,
    ]
  )

  return (
    <CarouselContext.Provider value={contextValue}>
      <div
        onKeyDownCapture={handleKeyDown}
        className={cn('position-relative', className)}
        role="region"
        aria-roledescription="carousel"
        data-slot="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  )
}

function CarouselControls() {
  const { scrollPrev, scrollNext, canScrollPrev, canScrollNext } = useCarousel()

  return (
    <div className="d-flex ">
      <button
        type="button"
        data-slot="carousel-previous"
        className="btn btn-link p-1 rounded-circle mr-2"
        style={{ opacity: canScrollPrev ? 1 : 0.4 }}
        disabled={!canScrollPrev}
        onClick={scrollPrev}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="14"
          viewBox="0 0 21 14"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M8.90654e-09 6.72333C3.98874e-09 7.13573 0.362033 7.47021 0.807925 7.47021L20.1921 7.47022C20.638 7.47022 20.9996 7.13573 20.9996 6.72333C20.9996 6.31093 20.6376 5.97618 20.1921 5.97618L0.807925 5.97618C0.362033 5.97618 1.38243e-08 6.31093 8.90654e-09 6.72333Z"
            fill="#505050"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M6.69792 0.218513L0.236315 6.1944C-0.0787717 6.48582 -0.0787717 6.95941 0.236315 7.25074L6.69792 13.2266C7.01291 13.518 7.52496 13.518 7.84005 13.2266C8.15504 12.9353 8.15504 12.4617 7.84005 12.1704L1.95032 6.7234L7.84147 1.27617C7.99821 1.1298 8.07733 0.938613 8.07733 0.747164C8.07733 0.555978 7.99821 0.364879 7.84005 0.218513C7.52468 -0.0728196 7.01291 -0.0728196 6.69792 0.218513Z"
            fill="#505050"
          />
        </svg>
        <span className="sr-only">Vorige slide</span>
      </button>
      <button
        type="button"
        data-slot="carousel-next"
        className="btn btn-link p-1 rounded-circle"
        style={{ opacity: canScrollNext ? 1 : 0.4 }}
        disabled={!canScrollNext}
        onClick={scrollNext}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="21"
          height="14"
          viewBox="0 0 21 14"
          fill="none"
        >
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M20.9996 6.72186C20.9996 6.30946 20.6376 5.97498 20.1917 5.97498L0.807547 5.97498C0.361656 5.97498 0 6.30946 0 6.72186C0 7.13426 0.362034 7.46901 0.807547 7.46901L20.1917 7.46901C20.6376 7.46901 20.9996 7.13426 20.9996 6.72186Z"
            fill="#505050"
          />
          <path
            fillRule="evenodd"
            clipRule="evenodd"
            d="M14.3017 13.2267L20.7633 7.25079C21.0784 6.95937 21.0784 6.48578 20.7633 6.19445L14.3017 0.218564C13.9867 -0.0728548 13.4747 -0.0728548 13.1596 0.218564C12.8446 0.509896 12.8446 0.983485 13.1596 1.27482L19.0493 6.72179L13.1581 12.169C13.0014 12.3154 12.9223 12.5066 12.9223 12.698C12.9223 12.8892 13.0014 13.0803 13.1596 13.2267C13.4749 13.518 13.9867 13.518 14.3017 13.2267Z"
            fill="#505050"
          />
        </svg>
        <span className="sr-only">Volgende slide</span>
      </button>
    </div>
  )
}

function CarouselContent({
  className,
  children,
  ...rest
}: React.ComponentProps<'div'>) {
  const { carouselRef } = useCarousel()

  return (
    <div
      ref={carouselRef}
      className="overflow-hidden"
      data-slot="carousel-content"
    >
      <div className={cn('d-flex', className)} {...rest}>
        {children}
      </div>
    </div>
  )
}

function CarouselItem({
  className,
  style,
  children,
  ...rest
}: React.ComponentProps<'div'>) {
  return (
    <div
      role="group"
      aria-roledescription="slide"
      data-slot="carousel-item"
      className={cn(className)}
      style={{
        flex: '0 0 auto',
        minWidth: 0,
        flexShrink: 0,
        flexGrow: 0,
        ...style,
      }}
      {...rest}
    >
      {children}
    </div>
  )
}

export {
  type CarouselApi,
  Carousel,
  CarouselControls,
  CarouselContent,
  CarouselItem,
}
